import React from "react";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../CustomTooltip";
import { computeForcedPermissions } from "./permissionCatalog";

// Controlled Read/Write permission grid. `selected` maps resource key ->
// { read, write }; changes emit via `onChange`. A row may drive several backing
// keys (toggled together). Invariants: Write forces Read on (only if the row has
// a Read level); Read off clears Write; a row with no Read renders write-only.
const PermissionMatrix = ({ groups, selected, onChange, disabled }) => {
  const { t } = useTranslation();

  const cellFor = (key) => selected[key] || { read: false, write: false };

  // Aggregate state for a (possibly multi-key) feature: on only when every
  // backing key is on.
  const getRead = (feature) =>
    feature.keys.length > 0 && feature.keys.every((k) => cellFor(k).read);
  const getWrite = (feature) =>
    feature.keys.length > 0 && feature.keys.every((k) => cellFor(k).write);

  // Returns a new `selected` with `level`=`turnOn` across the feature's backing
  // keys, applying the read/write coupling rules. Untouched keys (incl. hidden
  // grants) are preserved.
  const mutate = (base, feature, level, turnOn) => {
    const next = { ...base };
    feature.keys.forEach((key) => {
      const cell = { ...(next[key] || { read: false, write: false }) };
      if (level === "read") {
        cell.read = turnOn;
        if (!turnOn) cell.write = false; // read off => write off
      } else {
        cell.write = turnOn;
        if (turnOn && feature.availableRead) cell.read = true; // write => read
      }
      next[key] = cell;
    });
    return next;
  };

  const toggleRead = (feature) =>
    onChange(mutate(selected, feature, "read", !getRead(feature)));
  const toggleWrite = (feature) =>
    onChange(mutate(selected, feature, "write", !getWrite(feature)));

  // A feature's read/write may be force-enabled because another granted feature
  // bundles it (e.g. Check Card Balance requires sell_orders.read). Such a cell
  // is locked ON so the admin isn't misled into thinking their uncheck stuck.
  const forced = computeForcedPermissions(selected);
  const forcedBy = (feature, level) => {
    const labels = feature.keys.flatMap((k) => forced[`${k}.${level}`] || []);
    return [...new Set(labels)].filter((l) => l !== feature.name);
  };

  const readableFeatures = (group) =>
    group.features.filter((f) => f.availableRead);
  const writableFeatures = (group) =>
    group.features.filter((f) => f.availableWrite);

  const allRead = (group) => {
    const features = readableFeatures(group);
    return features.length > 0 && features.every((f) => getRead(f));
  };

  const allWrite = (group) => {
    const features = writableFeatures(group);
    return features.length > 0 && features.every((f) => getWrite(f));
  };

  const toggleAll = (group, level) => {
    const turnOn = level === "read" ? !allRead(group) : !allWrite(group);
    const features =
      level === "read" ? readableFeatures(group) : writableFeatures(group);
    let next = selected;
    features.forEach((feature) => {
      next = mutate(next, feature, level, turnOn);
    });
    onChange(next);
  };

  const cellStyle = {
    padding: "8px 12px",
    borderTop: "1px solid var(--form-input-border-color, #e0e0e0)",
  };
  const headerCellStyle = {
    padding: "8px 12px",
    fontWeight: 600,
  };

  return (
    <div className="permission-matrix">
      {groups.map((group) => (
        <div
          className="permission-group mb-4"
          key={group.key}
          style={{
            border: "1px solid var(--form-input-border-color, #e0e0e0)",
            borderRadius: "var(--form-input-border-radius, 6px)",
          }}
        >
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              padding: "8px 12px",
              background: "var(--multi-select-background-color, #f5f5f5)",
            }}
          >
            <strong>{t(group.label)}</strong>
            <div className="d-flex" style={{ gap: "24px" }}>
              <span className="checkbox-small">
                <input
                  type="checkbox"
                  id={`${group.key}-all-read`}
                  checked={allRead(group)}
                  disabled={disabled || readableFeatures(group).length === 0}
                  onChange={() => toggleAll(group, "read")}
                />
                <label htmlFor={`${group.key}-all-read`}>{t("All Read")}</label>
              </span>
              <span className="checkbox-small">
                <input
                  type="checkbox"
                  id={`${group.key}-all-write`}
                  checked={allWrite(group)}
                  disabled={disabled || writableFeatures(group).length === 0}
                  onChange={() => toggleAll(group, "write")}
                />
                <label htmlFor={`${group.key}-all-write`}>{t("All Write")}</label>
              </span>
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...headerCellStyle, textAlign: "left" }}>
                  {t("Feature")}
                </th>
                <th style={{ ...headerCellStyle, textAlign: "center", width: "100px" }}>
                  {t("Read")}
                </th>
                <th style={{ ...headerCellStyle, textAlign: "center", width: "100px" }}>
                  {t("Write")}
                </th>
              </tr>
            </thead>
            <tbody>
              {group.features.map((feature) => (
                <tr key={feature.id}>
                  <td style={cellStyle}>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      {t(feature.name)}
                      {feature.supporting && feature.supporting.length > 0 && (
                        <span style={{ marginLeft: "6px", display: "inline-flex" }}>
                          <CustomTooltip
                            message={`${t("Also grants")}: ${feature.supporting.join(", ")}`}
                          />
                        </span>
                      )}
                    </span>
                    {feature.description && (
                      <div
                        style={{
                          color: "var(--virtual-card-text, #6F808B)",
                          fontSize: "0.8rem",
                          marginTop: "2px",
                        }}
                      >
                        {t(feature.description)}
                      </div>
                    )}
                    {feature.warning && (
                      <div
                        style={{
                          color: "var(--warning-text, #B54708)",
                          fontSize: "0.75rem",
                          marginTop: "4px",
                          fontWeight: 500,
                        }}
                      >
                        {t("Note")}: {t(feature.warning)}
                      </div>
                    )}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    {feature.availableRead &&
                      (() => {
                        const req = forcedBy(feature, "read");
                        const locked = req.length > 0;
                        return (
                          <span
                            className="checkbox-small"
                            title={
                              locked
                                ? `${t("Required by")}: ${req.join(", ")}`
                                : undefined
                            }
                          >
                            <input
                              type="checkbox"
                              id={`${feature.id}-read`}
                              checked={getRead(feature) || locked}
                              disabled={disabled || locked}
                              onChange={() => toggleRead(feature)}
                            />
                            <label
                              htmlFor={`${feature.id}-read`}
                              aria-label={`${feature.name} read`}
                            />
                          </span>
                        );
                      })()}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    {feature.availableWrite &&
                      (() => {
                        const req = forcedBy(feature, "write");
                        const locked = req.length > 0;
                        return (
                          <span
                            className="checkbox-small"
                            title={
                              locked
                                ? `${t("Required by")}: ${req.join(", ")}`
                                : undefined
                            }
                          >
                            <input
                              type="checkbox"
                              id={`${feature.id}-write`}
                              checked={getWrite(feature) || locked}
                              disabled={disabled || locked}
                              onChange={() => toggleWrite(feature)}
                            />
                            <label
                              htmlFor={`${feature.id}-write`}
                              aria-label={`${feature.name} write`}
                            />
                          </span>
                        );
                      })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default PermissionMatrix;
